import type { CatalogueKind, UserRole } from '@prisma/client'

import { ADMIN_ROLES, holdsAny } from './user-roles'

/**
 * Who may change the Kalkulation: how a price is computed — a measure's
 * calculation columns, the stages it is computed in, the blocks and templates
 * they are built from, and the formula library its expressions read.
 *
 * Every role that opens the catalogue reads it; only an administrator changes
 * it, because it decides what every project is quoted. What a price is
 * computed from — a product's price, a unit — stays with whoever edits the
 * catalogue. The server refuses the write, and the screens disable themselves.
 *
 * One list, read two ways: `mayEditCalculation` answers for a payload of
 * measure columns, and `CalculationRoles()` gates the routes that write the
 * stages, the blocks and the templates — those name no measure column, so the
 * payload guard cannot see them. Written out once, so the two cannot drift the
 * day "the Kalkulation" stops meaning "an administrator".
 */
export const CALCULATION_ROLES = ADMIN_ROLES

export const mayEditCalculation = (
  roles: readonly UserRole[] | null | undefined,
): boolean => holdsAny(roles, CALCULATION_ROLES)

/**
 * Which branches of the catalogue tree hold what a price is computed by, with
 * what a refusal calls each.
 *
 * Exhaustive over the kinds, so a sixth branch has to decide which side it is
 * on rather than defaulting to "anybody who edits the catalogue". Read by the
 * backend guard on the routes that serve every branch — the folders, the bulk
 * delete, the bulk move — and by the catalogue screen, which puts itself in a
 * read-only scope for the same branches. Named only on the server, the screen
 * offered the gestures the server then refused.
 */
export const CALCULATION_BRANCHES = {
  MEASURE: null,
  PRODUCT: null,
  LABOUR: null,
  FORMULA: 'the formula library',
  PRICE_BLOCK: 'the Preisbausteine',
  PRICE_GROUP: 'the Preisgruppen',
} as const satisfies Record<CatalogueKind, string | null>

/** Whether a branch is one of them. */
export const isCalculationBranch = (kind: CatalogueKind): boolean =>
  CALCULATION_BRANCHES[kind] != null

/** The branches themselves, for a reader that iterates them rather than
 *  asking about one. */
export const CALCULATION_BRANCH_KINDS = (
  Object.keys(CALCULATION_BRANCHES) as CatalogueKind[]
).filter(isCalculationBranch)
